import axios from 'axios';

import PohlconAuthService from './PohlconAuthService';

import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
export function isAxiosError(value: any): value is AxiosError {
    return typeof value?.response === 'object';
}

export default abstract class PohlconApiService {

    protected readonly http: AxiosInstance;

    protected constructor(protected readonly path?: string) {
        let baseURL = import.meta.env.VITE_API_URL || '';
        if (path) 
            baseURL += path;
        this.http = axios.create({
            baseURL,
        });

        this.appendDefaultHeaders();
        this.removeEmptyParams();
        this.appendTokens();
    }

    /**
     * Appends default headers to the HTTP request.
     * Sets the 'Accept' and 'Content-Type' headers to 'application/json;charset=UTF-8'.
     */
    protected appendDefaultHeaders() {
        this.http.defaults.headers.common['Accept'] = 'application/json;charset=UTF-8';
        this.http.defaults.headers.common['Content-Type'] = 'application/json;charset=UTF-8';
    }
    
    /**
     * Removes empty parameters from the HTTP request configuration.
     * @remarks
     * This method is used as an interceptor for the HTTP request.
     * It checks for empty or undefined parameters in the request configuration
     * and removes them before sending the request.
     */
    protected removeEmptyParams() {
        this.http.interceptors.request.use((config) => {
            if (!config.params)
                return config;

            for (const param in config.params) {
              if (config.params[param] === '' || config.params[param] === undefined) {
                delete config.params[param];
              }
            }
            return config;
        });
    }
    /**
     * Appends tokens to the HTTP request headers.
     */
    protected appendTokens() {
        this.http.interceptors.request.use((config) => {
            const clientId = import.meta.env.VITE_API_CLIENT_ID;
            const token = localStorage.getItem(`${clientId}_bearer`);
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        });
    }

    /**
     * Handles the response from an API request.
     * @param response The AxiosResponse object containing the response data.
     * @returns The data from the response.
     * @template T The type of the response data.
     */
    protected handleResponse<T>(response: AxiosResponse<T>): T {
        return response.data;
    }

    /**
     * Handles errors that occur during API requests.
     * @param error - The error object.
     * @returns A promise that never resolves, indicating an error.
     */
    protected async handleError(error: any): Promise<never> {
        error.origin = 'axios';
        if (error instanceof Error) {
            if (isAxiosError(error)) {
                if (error.response) {
                    if (error.response.status == 401) {
                        window.location.href = await (new PohlconAuthService()).getAuthorizationUrl();
                    }
                    if (error.response.status == 403) {
                        window.alert('You are not allowed to access this resource.')
                    }

                } else if (error.request) {
                    throw new Error(error as any);
                }
            } else {
                throw new Error(error.message);
            }
        }
        throw new Error(error as any);
    }
    
}
